import app, { auth } from '../firebase.js';

// The result-template endpoints live in the `templates` Firebase codebase (Python), not in
// `functions/` (Node), because the kiosk renders result.html with Jinja2 and the gate in
// front of Firestore has to run the same engine. They are onRequest rather than callable so
// the editor can tell a 422 (the draft does not compile) apart from a 500.
const REGION = 'europe-west1';

// These are onRequest endpoints called with fetch, so connectFunctionsEmulator() -- which
// only reroutes httpsCallable -- does not reach them. Set
// REACT_APP_FUNCTIONS_EMULATOR_ORIGIN (e.g. http://127.0.0.1:5001) to point them at a local
// emulator instead; see functions-python/README.md.
const EMULATOR_ORIGIN = process.env.REACT_APP_FUNCTIONS_EMULATOR_ORIGIN;

// Resolved per call rather than at import: tests that mock firebase.js have no initialized
// app, and a module-level read would fail the whole suite on import.
const functionUrl = (functionName) => {
    const projectId = app?.options?.projectId;

    return EMULATOR_ORIGIN
        ? `${EMULATOR_ORIGIN.replace(/\/$/, '')}/${projectId}/${REGION}/${functionName}`
        : `https://${REGION}-${projectId}.cloudfunctions.net/${functionName}`;
};

export const SAMPLE_LONGEST_CELL = 'longest_cell';
export const SAMPLE_RANDOM = 'random';

export class ResultTemplateApiError extends Error {
    constructor(message, { status, errors = [], warnings = [] } = {}) {
        super(message);
        this.name = 'ResultTemplateApiError';
        this.status = status;
        this.errors = errors;
        this.warnings = warnings;
    }
}

const requestJson = async (functionName, { method = 'POST', body } = {}) => {
    const user = auth.currentUser;
    if (!user) {
        throw new ResultTemplateApiError('Du er ikke logget ind.', { status: 401 });
    }

    const response = await fetch(functionUrl(functionName), {
        method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
        // 422 carries the same {valid, errors, warnings} report the save gate produces, so
        // the editor renders one error panel for both paths.
        throw new ResultTemplateApiError(
            payload.error || 'Skabelonen kunne ikke behandles.',
            { status: response.status, errors: payload.errors || [], warnings: payload.warnings || [] },
        );
    }

    return payload;
};

export const validateResultTemplate = ({ html, css }) =>
    requestJson('validateResultTemplate', { body: { html, css } });

export const fetchResultTemplateDefaults = () =>
    requestJson('resultTemplateDefaults', { method: 'GET' });

// read_aloud is left unset: the endpoint defaults it to false, and the editor has no reason
// to make a preview speak — it is clicked repeatedly while comparing layouts.
export const previewResultTemplate = ({ wasteReceiverId, html, css, sample, rowIndex }) =>
    requestJson('previewResultTemplate', {
        body: {
            waste_receiver_id: wasteReceiverId,
            html,
            css,
            sample,
            // Pins the row: the editor sends back the index a previous preview returned so
            // that re-rendering after an edit compares the same row instead of re-scanning.
            // Omitted rather than sent as null, which the endpoint would reject as a
            // non-integer row_index.
            ...(Number.isInteger(rowIndex) ? { row_index: rowIndex } : {}),
            // fonts.css reaches its font files by a path relative to the kiosk's static
            // directory, which resolves to nothing inside a srcdoc iframe. The same files
            // are published under this app's hosting, so the renderer rewrites the
            // @font-face urls against this origin.
            asset_base_url: window.location.origin,
        },
    });
